import React from 'react'
import { useTheme } from '@mui/material/styles'
import { useDispatch } from 'react-redux'
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  IconButton,
} from '@mui/material'
import {
  LocationOn as LocationOnIcon,
  AttachMoney as AttachMoneyIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material'
import { formatSalary, getPracticeTypeString } from './helpers'
import { updateAiJobFavorite } from '/src/redux/aiJobSearch'
import JobFeedback from './JobFeedback'

// Match tier color mapping
const getMatchTierColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent':
      return {
        bg: theme.palette.success.light,
        color: theme.palette.success.dark,
        border: theme.palette.success.main,
      }
    case 'strong':
      return {
        bg: theme.palette.info.light,
        color: theme.palette.info.dark,
        border: theme.palette.info.main,
      }
    case 'good':
      return {
        bg: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        border: theme.palette.warning.main,
      }
    case 'moderate':
    default:
      return {
        bg: theme.palette.grey[200],
        color: theme.palette.grey[700],
        border: theme.palette.grey[400],
      }
  }
}

const AIJobCard = ({
  rankedJob,
  isActive,
  onClick,
  onFavoriteClick,
  conversationId,
}) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const job = rankedJob?.job
  const matchTier = rankedJob?.matchTier
  const justification = rankedJob?.justification

  if (!job) return null

  const matchTierColors = getMatchTierColor(matchTier, theme)
  const isFavorite = job.candidateJob?.favorite

  const feedbackElements = JobFeedback({
    jobId: job.id,
    conversationId,
    chatbotMessageId: rankedJob.chatbotMessageId,
    matchTier,
    feedback: rankedJob.feedback,
  })

  const handleFavoriteClick = (e) => {
    e.stopPropagation()
    const newFavoriteState = !isFavorite

    // Update local Redux state
    dispatch(updateAiJobFavorite({
      jobId: job.id,
      favorite: newFavoriteState,
    }))

    // Call parent handler if provided (to persist to server)
    if (onFavoriteClick) {
      onFavoriteClick(job.id, newFavoriteState)
    }
  }

  return (
    <Card
      onClick={onClick}
      sx={{
        width: '100%',
        borderRadius: 1,
        border: isActive
          ? `2px solid ${theme.palette.ai.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isActive ? theme.palette.shadows.medium : theme.palette.shadows.light,
        cursor: 'pointer',
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        '&:hover': {
          boxShadow: theme.palette.shadows.medium,
          borderColor: theme.palette.ai.light,
          transition: 'all 0.2s ease-in-out',
          transform: 'translateZ(0)',
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1}>
          {/* Match Tier Badge and Favorite */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {matchTier && (
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: '0.9rem !important' }} />}
                label={matchTier}
                size='small'
                sx={{
                  backgroundColor: matchTierColors.bg,
                  color: matchTierColors.color,
                  borderColor: matchTierColors.border,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  height: '24px',
                  '& .MuiChip-icon': {
                    color: matchTierColors.color,
                  },
                }}
              />
            )}
            <IconButton
              size="small"
              onClick={handleFavoriteClick}
              sx={{
                color: isFavorite ? theme.palette.error.main : theme.palette.grey[400],
                '&:hover': {
                  color: theme.palette.error.main,
                },
              }}
            >
              {isFavorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
            </IconButton>
          </Box>

          {/* Job Title and Employer */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='subtitle1'
                sx={{
                  color: theme.palette.primary.dark,
                  fontWeight: 'medium',
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {job.title}
              </Typography>
              {job.employer?.name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon fontSize='small' color='action' />
                  <Typography variant='body2' color='text.secondary' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {job.employer.name}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexWrap: 'wrap' }}>
              {job.practiceType && getPracticeTypeString(job.practiceType) && (
                <Chip
                  label={getPracticeTypeString(job.practiceType)}
                  size='small'
                  variant='outlined'
                  sx={{ height: '24px', fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>

          {/* Location and Salary - Combined Row */}
          {(job.city || job.state) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOnIcon fontSize='small' color='action' />
                <Typography variant='body2'>
                  {job.city}{job.city && job.state && ', '}{job.state}
                </Typography>
              </Box>

              {(job.salary || job.salaryMin || job.salaryMax) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoneyIcon fontSize='small' color='action' />
                  <Typography variant='body2'>
                    {formatSalary(job.salary || job.salaryMin || job.salaryMax)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Specialties and Candidate Type */}
          {(job.specialties?.length > 0 || job.candidateType?.name) && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              {/* Specialties Column */}
              {job.specialties?.length > 0 && (
                <Box sx={{ minWidth: '40%' }}>
                  <Typography
                    variant='caption'
                    sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}
                  >
                    Specialties
                  </Typography>
                  <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
                    {job.specialties.slice(0, 2).map(specialty => (
                      <Chip
                        key={specialty?.id}
                        label={specialty?.name}
                        size='small'
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                    ))}
                    {job.specialties.length > 2 && (
                      <Chip
                        label={`+${job.specialties.length - 2}`}
                        size='small'
                        variant='outlined'
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                      />
                    )}
                  </Stack>
                </Box>
              )}

              {/* Candidate Type Column */}
              {job.candidateType?.name && (
                <Box sx={{ minWidth: '40%' }}>
                  <Typography
                    variant='caption'
                    sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}
                  >
                    Position Type
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SchoolIcon fontSize='small' color='action' />
                    <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                      {job.candidateType.name}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Key Benefits */}
          {(job.visaSponsorship || job.loanForgiveness || job.pslfCompatible) && (
            <Box>
              <Typography
                variant='caption'
                sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}
              >
                Benefits
              </Typography>
              <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
                {job.visaSponsorship && (
                  <Chip
                    label='Visa Sponsorship'
                    size='small'
                    variant='outlined'
                    color='success'
                    sx={{ height: '20px', fontSize: '0.7rem' }}
                  />
                )}
                {job.loanForgiveness && (
                  <Chip
                    label='Loan Forgiveness'
                    size='small'
                    variant='outlined'
                    color='success'
                    sx={{ height: '20px', fontSize: '0.7rem' }}
                  />
                )}
                {job.pslfCompatible && (
                  <Chip
                    label='PSLF Eligible'
                    size='small'
                    variant='outlined'
                    color='success'
                    sx={{ height: '20px', fontSize: '0.7rem' }}
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* AI Justification */}
          {justification && (
            <>
              <Box
                sx={{
                  backgroundColor: `${matchTierColors.bg}`,
                  borderRadius: 1,
                  p: 1,
                  borderLeft: `3px solid ${matchTierColors.border}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography
                    variant='caption'
                    sx={{
                      color: matchTierColors.color,
                      fontWeight: 'bold',
                    }}
                  >
                    Why this match
                  </Typography>
                  {feedbackElements.thumbs}
                </Box>
                <Typography variant='body2' sx={{ fontSize: '0.85rem', color: theme.palette.text.secondary }}>
                  {justification}
                </Typography>
              </Box>

              {/* Job feedback panel below justification */}
              {feedbackElements.panel}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default AIJobCard
